// AIHELP1 (docs/build-briefs/AIHELP1-willy-ai-support-chat.md; design/style-guide.md sections 3, 7, 8, 10, 11, 12, 13; owner ruling
// DEC-574): Willy, the AI support chat of the location and franchisor consoles. A round conversation-bubble button, bottom right,
// opens a docked panel on the right. Like the Help panel it is never a modal: the screen and any draft stay as they are underneath,
// and the panel makes room instead of covering (styles.css reads body[data-help-open]). The bubble hides while a sheet or the Help
// panel is open. Opening Willy closes the Help panel and the other way round, so the right edge holds one panel.
//
// The conversation lives in this closure for the session only and is dropped on sign out. Every shown word is set with textContent;
// the answer text is plain text. The console hands in its own request helper (its own session) and its own way to name the screen.

import type { HelpTarget } from "./helpPanel.ts";
import type { SupportPrefill } from "./supportHelp.ts";
import { personAvatar, willyAvatar } from "./willyAvatar.ts";
import {
  WILLY_BUSY_LINE,
  WILLY_GREETING,
  WILLY_RATE_LIMITED_LINE,
  WILLY_TRY_AGAIN_LINE,
  WILLY_UNAVAILABLE_LINE,
  historyForServer,
  isWillyAnswer,
  questionError,
  supportPrefill,
  type WillyMessage,
} from "./willyModel.ts";

export interface WillyHost {
  /** One call to this console's Willy routes: "/status" (GET) or "/turn" (POST). A 401 has already ended the session. */
  request<T>(method: "GET" | "POST", suffix: string, body?: unknown): Promise<{ status: number; body: T | null }>;
  /** The guide section for the screen on show right now (the Help panel's own screen map). */
  target(): HelpTarget;
  /** Opens the Get help question form with this text in it. */
  contactSupport(prefill: SupportPrefill): void;
  /** Closes the Help panel if it is open. */
  closeHelp(): void;
  /** Opens the Help panel on a guide section. */
  openGuide(target: HelpTarget): void;
  /** Whether the signed-in person may have Willy on the screen on show: the permission and a non-floor screen. */
  canShow(): boolean;
  /** WILLYFACE1: the signed-in person's full name, for the initials in their round avatar. */
  personName(): string;
}

export interface WillyChat {
  /** Re-reads whether the bubble belongs on this screen. Call after sign in, a permission refresh and every address change. */
  refreshVisibility(): void;
  close(): void;
  isOpen(): boolean;
  /** Signed out: closes the panel and forgets the conversation. */
  reset(): void;
}

function make<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function createWillyChat(host: WillyHost): WillyChat {
  const bubble = make("button", "willy-bubble");
  bubble.type = "button";
  bubble.hidden = true;
  bubble.dataset["testid"] = "willy-bubble";
  bubble.setAttribute("aria-label", "Chat with Willy");
  bubble.setAttribute("aria-expanded", "false");
  // A plain speech-bubble outline; the button's name is the aria-label.
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("aria-hidden", "true");
  icon.setAttribute("focusable", "false");
  const outline = document.createElementNS("http://www.w3.org/2000/svg", "path");
  outline.setAttribute("d", "M4 5h16v11H9l-5 4z");
  outline.setAttribute("fill", "none");
  outline.setAttribute("stroke", "currentColor");
  outline.setAttribute("stroke-width", "2");
  outline.setAttribute("stroke-linejoin", "round");
  icon.append(outline);
  bubble.append(icon);
  document.body.append(bubble);

  /** HIGFIX3 (F-16; design/style-guide.md "Console header"): the button is a header item, left of Help, so it never floats over
   * a table row or a column. Only a screen with no visible Help button (no console header) keeps it floating bottom right. */
  function placeBubble(): void {
    const help = Array.from(document.querySelectorAll<HTMLElement>(".topbar-help")).find((node) => node.offsetParent !== null);
    if (help !== undefined) {
      if (bubble.nextElementSibling !== help) help.before(bubble);
      bubble.classList.add("willy-in-header");
    } else {
      if (bubble.parentElement !== document.body) document.body.append(bubble);
      bubble.classList.remove("willy-in-header");
    }
  }

  let panel: HTMLElement | null = null;
  let log: HTMLElement;
  let input: HTMLTextAreaElement;
  let sendButton: HTMLButtonElement;
  let inputError: HTMLElement;
  let composer: HTMLElement;
  let unavailableBox: HTMLElement;
  let messages: WillyMessage[] = [];
  let busy = false;
  let failed: "try" | "limit" | null = null;
  let unavailable = false;
  // Bumped on reset, so a reply that arrives after sign out is dropped.
  let generation = 0;

  function build(): HTMLElement {
    const aside = make("aside", "willy-panel");
    aside.dataset["testid"] = "willy-panel";
    aside.setAttribute("role", "complementary");
    aside.setAttribute("aria-labelledby", "willy-panel-title");
    aside.hidden = true;

    const head = make("header", "willy-head");
    const title = make("h2", "willy-title", "Willy");
    title.id = "willy-panel-title";
    const close = make("button", "btn-secondary btn-compact", "Close");
    close.type = "button";
    close.dataset["testid"] = "willy-close";
    close.addEventListener("click", () => api.close());
    head.append(willyAvatar(40), title, close);

    log = make("div", "willy-log");
    log.dataset["testid"] = "willy-log";
    log.setAttribute("role", "log");
    log.setAttribute("aria-live", "polite");
    log.setAttribute("aria-label", "Conversation with Willy");

    unavailableBox = make("div", "willy-unavailable");
    unavailableBox.dataset["testid"] = "willy-unavailable";
    unavailableBox.hidden = true;
    const unavailableLine = make("p", "willy-note", WILLY_UNAVAILABLE_LINE);
    const contact = make("button", "btn-primary", "Contact support");
    contact.type = "button";
    contact.dataset["testid"] = "willy-unavailable-contact";
    contact.addEventListener("click", () => host.contactSupport(supportPrefill(messages)));
    unavailableBox.append(unavailableLine, contact);

    composer = make("div", "willy-composer");
    const label = make("label", "field-label", "Ask Willy");
    label.htmlFor = "willy-input";
    input = make("textarea", "field-input willy-input");
    input.id = "willy-input";
    input.rows = 3;
    input.inputMode = "text";
    input.autocomplete = "off";
    input.dataset["testid"] = "willy-input";
    inputError = make("p", "willy-error");
    inputError.hidden = true;
    inputError.setAttribute("role", "alert");
    inputError.dataset["testid"] = "willy-input-error";
    sendButton = make("button", "btn-primary", "Send");
    sendButton.type = "button";
    sendButton.dataset["testid"] = "willy-send";
    sendButton.addEventListener("click", () => void send());
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        void send();
      }
    });
    // WILLYFACE1: Send sits flush right of the box, on its row, at a 44pt target.
    const row = make("div", "willy-send-row");
    row.append(input, sendButton);
    composer.append(label, row, inputError);

    // Narrow screens show the panel full width (styles.css); this is the clear way back to the screen under it.
    const back = make("button", "btn-secondary btn-compact willy-back", "Back to screen");
    back.type = "button";
    back.dataset["testid"] = "willy-back";
    back.addEventListener("click", () => api.close());
    head.insertBefore(back, close);

    aside.append(head, log, unavailableBox, composer);
    aside.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        api.close();
      }
    });
    document.body.append(aside);
    return aside;
  }

  /** One line of the conversation: Willy's face then his bubble, or the person's bubble then their initials. */
  function rowFor(from: "user" | "assistant", bubbleNode: HTMLElement): HTMLElement {
    const row = make("div", `willy-row willy-row-${from}`);
    if (from === "assistant") row.append(willyAvatar(32), bubbleNode);
    else row.append(bubbleNode, personAvatar(host.personName(), 32));
    return row;
  }

  function messageNode(message: WillyMessage): HTMLElement {
    const node = make("div", `willy-msg willy-msg-${message.from}`);
    node.dataset["testid"] = message.from === "user" ? "willy-msg-user" : "willy-msg-willy";
    node.append(make("p", "willy-msg-text", message.text));
    if (message.from === "assistant") {
      const { source } = message;
      if (source !== undefined && source !== null) {
        const from = make("button", "willy-source", `From the guide: ${source.title}`);
        from.type = "button";
        from.dataset["testid"] = "willy-source";
        from.addEventListener("click", () => {
          api.close();
          host.openGuide(source.section === undefined ? { slug: source.slug } : { slug: source.slug, section: source.section });
        });
        node.append(from);
      }
      const stuck = make("button", "willy-stuck", "Still stuck? Contact support");
      stuck.type = "button";
      stuck.dataset["testid"] = "willy-contact";
      stuck.addEventListener("click", () => host.contactSupport(supportPrefill(messages)));
      node.append(stuck);
    }
    return rowFor(message.from, node);
  }

  function draw(): void {
    const nodes: HTMLElement[] = [];
    const greeting = make("div", "willy-msg willy-msg-assistant");
    greeting.dataset["testid"] = "willy-greeting";
    greeting.append(make("p", "willy-msg-text", WILLY_GREETING));
    nodes.push(rowFor("assistant", greeting));
    for (const message of messages) nodes.push(messageNode(message));
    if (busy) {
      // Three bouncing dots in Willy's bubble (still under prefers-reduced-motion); the words are for screen readers.
      const wait = make("div", "willy-msg willy-msg-assistant willy-typing");
      wait.dataset["testid"] = "willy-busy";
      const dots = make("span", "willy-dots");
      dots.setAttribute("aria-hidden", "true");
      dots.append(make("span"), make("span"), make("span"));
      wait.append(dots, make("span", "willy-sr-only", WILLY_BUSY_LINE));
      nodes.push(rowFor("assistant", wait));
    }
    if (failed !== null) {
      const box = make("div", "willy-failed");
      box.dataset["testid"] = "willy-failed";
      box.append(make("p", "willy-error", failed === "limit" ? WILLY_RATE_LIMITED_LINE : WILLY_TRY_AGAIN_LINE));
      if (failed === "try") {
        const again = make("button", "btn-secondary btn-compact", "Try again");
        again.type = "button";
        again.dataset["testid"] = "willy-retry";
        again.addEventListener("click", () => void ask());
        box.append(again);
      }
      nodes.push(box);
    }
    log.replaceChildren(...nodes);
    log.scrollTop = log.scrollHeight;
    sendButton.disabled = busy;
    input.disabled = unavailable;
    composer.hidden = unavailable;
    unavailableBox.hidden = !unavailable;
  }

  async function ask(): Promise<void> {
    if (busy) return;
    const mine = generation;
    busy = true;
    failed = null;
    draw();
    const reply = await host.request<unknown>("POST", "/turn", { history: historyForServer(messages), screen: host.target() });
    if (mine !== generation) return;
    busy = false;
    if (reply.status === 200 && isWillyAnswer(reply.body)) {
      messages = [
        ...messages,
        { from: "assistant", text: reply.body.answer, source: reply.body.source, answered: reply.body.answered },
      ];
    } else if (reply.status === 503) {
      unavailable = true;
    } else if (reply.status === 429) {
      failed = "limit";
    } else if (reply.status !== 401) {
      failed = "try";
    }
    draw();
    // Focus never rests on a button that was just disabled: it goes to the next thing to act on.
    if (failed === "try") log.querySelector<HTMLElement>('[data-testid="willy-retry"]')?.focus();
    else if (!unavailable && failed === null) input.focus();
  }

  async function send(): Promise<void> {
    if (busy) return;
    const problem = questionError(input.value);
    inputError.textContent = problem ?? "";
    inputError.hidden = problem === undefined;
    if (problem !== undefined) return;
    messages = [...messages, { from: "user", text: input.value.trim() }];
    input.value = "";
    await ask();
  }

  async function readStatus(): Promise<void> {
    const mine = generation;
    const reply = await host.request<{ available?: unknown }>("GET", "/status");
    if (mine !== generation || reply.status !== 200 || reply.body === null) return;
    if (reply.body.available === false) {
      unavailable = true;
      draw();
    }
  }

  const api: WillyChat = {
    refreshVisibility() {
      placeBubble();
      const show = host.canShow();
      bubble.hidden = !show;
      if (!show && api.isOpen()) api.close();
    },
    close() {
      if (panel === null || panel.hidden) return;
      panel.hidden = true;
      document.body.removeAttribute("data-help-open");
      document.body.removeAttribute("data-willy-open");
      bubble.setAttribute("aria-expanded", "false");
      bubble.focus();
    },
    isOpen() {
      return panel !== null && !panel.hidden;
    },
    reset() {
      generation += 1;
      messages = [];
      busy = false;
      failed = null;
      unavailable = false;
      document.body.removeAttribute("data-willy-open");
      if (panel !== null && !panel.hidden) document.body.removeAttribute("data-help-open");
      panel?.remove();
      panel = null;
      bubble.hidden = true;
      bubble.setAttribute("aria-expanded", "false");
    },
  };

  function open(): void {
    host.closeHelp();
    if (panel === null) panel = build();
    panel.hidden = false;
    document.body.setAttribute("data-help-open", "");
    document.body.setAttribute("data-willy-open", "");
    bubble.setAttribute("aria-expanded", "true");
    // Asked on every open, so a provider switched in Snip & Sip Admin or a spent budget shows without a reload.
    unavailable = false;
    draw();
    input.focus();
    void readStatus();
  }

  bubble.addEventListener("click", () => (api.isOpen() ? api.close() : open()));
  window.addEventListener("hashchange", () => api.refreshVisibility());
  return api;
}
